import { isSafari } from "@memorang/helpers/platform";
import {
	setAudioModeAsync,
	useAudioPlayer,
	useAudioPlayerStatus,
} from "expo-audio";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Platform } from "react-native";

const TIMEOUT_DURATION_MS = 10000;

const usePlayer = (
	source: string | null,
	totalTimeInMilliseconds?: number,
	autoPlay?: boolean,
) => {
	const [isEnded, setIsEnded] = useState(false);
	const [hasError, setHasError] = useState(false);

	const audioErrorTimeout = useRef<ReturnType<typeof setTimeout>>(undefined);
	const hasAutoPlayed = useRef(false);

	const player = useAudioPlayer(null, {
		updateInterval: 100,
	});

	const status = useAudioPlayerStatus(player);

	const prevSourceRef = useRef<string | null>(null);
	useEffect(() => {
		if (source !== prevSourceRef.current) {
			prevSourceRef.current = source;
			hasAutoPlayed.current = false;
			if (source) {
				player.replace({ uri: source });
			}
		}
	}, [source, player]);

	const playSound = useCallback(async () => {
		if (isSafari && source) {
			player.replace({ uri: source });
		}
		setIsEnded(false);
		await setAudioModeAsync({
			allowsRecording: false,
			playsInSilentMode: true,
			shouldRouteThroughEarpiece: false,
		});
		player.play();
	}, [source, player]);

	const pauseSound = useCallback(() => {
		player.pause();
	}, [player]);

	const onSeek = useCallback(
		async (timeMs: number) => {
			await player.seekTo(timeMs / 1000);
			setIsEnded(false);
		},
		[player],
	);

	const setRate = useCallback(
		(rate: number) => {
			player.setPlaybackRate(rate, "high");
		},
		[player],
	);

	const unloadSound = useCallback(() => {
		player.remove();
	}, [player]);

	const isWeb = Platform.OS === "web";
	const [rafTimeMs, setRafTimeMs] = useState(0);
	useEffect(() => {
		if (!(status.playing && isWeb)) {
			return;
		}
		let rafId: number;
		const poll = () => {
			setRafTimeMs(Math.round(player.currentTime * 1000));
			rafId = requestAnimationFrame(poll);
		};
		rafId = requestAnimationFrame(poll);
		return () => {
			cancelAnimationFrame(rafId);
		};
	}, [status.playing, player, isWeb]);

	const currentTimeInMilliseconds =
		status.playing && isWeb ? rafTimeMs : Math.round(status.currentTime * 1000);

	const statusDurationMs =
		!Number.isNaN(status.duration) &&
		status.duration !== Number.POSITIVE_INFINITY &&
		status.duration > 0
			? Math.round(status.duration * 1000)
			: 0;

	const computedDuration = totalTimeInMilliseconds || statusDurationMs;

	useEffect(() => {
		if (status.didJustFinish) {
			setIsEnded(true);
		} else if (
			status.isLoaded &&
			statusDurationMs > 0 &&
			currentTimeInMilliseconds >= statusDurationMs - 200
		) {
			setIsEnded(true);
		}
	}, [
		status.didJustFinish,
		status.isLoaded,
		statusDurationMs,
		currentTimeInMilliseconds,
	]);

	useEffect(() => {
		if (source && !(status.isBuffering || status.isLoaded)) {
			audioErrorTimeout.current = setTimeout(() => {
				setHasError(true);
			}, TIMEOUT_DURATION_MS);
		} else {
			setHasError(false);
		}

		return () => clearTimeout(audioErrorTimeout.current);
	}, [source, status.isBuffering, status.isLoaded]);

	useEffect(() => {
		if (autoPlay && status.isLoaded && !hasAutoPlayed.current) {
			hasAutoPlayed.current = true;
			playSound();
		}
	}, [autoPlay, status.isLoaded, playSound]);

	const returnValue = useMemo(
		() => ({
			playSound,
			pauseSound,
			isPlaying: status.playing,
			currentTimeInMilliseconds,
			totalTimeInMilliseconds: computedDuration,
			isBuffering: status.isBuffering,
			onSeek,
			setRate,
			isEnded,
			isLoaded: status.isLoaded,
			hasError,
			unloadSound,
		}),
		[
			playSound,
			pauseSound,
			status.playing,
			currentTimeInMilliseconds,
			computedDuration,
			status.isBuffering,
			onSeek,
			setRate,
			isEnded,
			status.isLoaded,
			hasError,
			unloadSound,
		],
	);

	return returnValue;
};

export default usePlayer;
